import React, { useState } from 'react';
import { Callout, Icon, Dialog } from '@scale/scaleui-radix';
import { SelectMenu, Button, Typography } from '@scale/scaleui';
import { ChatBulkAuditTaskAction, REVIEW_LEVEL_MAP } from '@scale/shared/types/corp/attemptAudit';
import { AttemptAuditResults } from 'react-scale/components/audits/SpeedAudit/types';
import { AuditResultV2 } from '@scale/shared/defaults';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
const actionOptions = [
  {
    id: ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_CLEAR_RESPONSES,
    name: 'Send Back to Queue (Clear Responses)',
  },
  {
    id: ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_KEEP_RESPONSES,
    name: 'Send to Specific Review Level (Keep Responses)',
  },
  { id: ChatBulkAuditTaskAction.KEEP_IN_CURRENT_LEVEL, name: 'Keep in Current Level' },
  { id: ChatBulkAuditTaskAction.MOVE_FORWARD_ONE_LAYER, name: 'Move Forward One Layer' },
];

const ratingOptions = [
  { id: AuditResultV2.Perfect, name: '5 - Perfect Tasks' },
  { id: AuditResultV2.Good, name: '4 - Good Tasks' },
  { id: AuditResultV2.Ok, name: '3 - Ok Tasks' },
  { id: AuditResultV2.Bad, name: '2 - Bad Tasks' },
  { id: AuditResultV2.Terrible, name: '1 - Terrible Tasks' },
];

const reviewLevelOptions = Object.entries(REVIEW_LEVEL_MAP)
  .map(([key, value]) => ({
    id: key,
    name: value.toString(),
  }))
  .sort((a, b) => Number(a.name) - Number(b.name));

interface BulkAssignTaskActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  attemptAudits: AttemptAuditResults[];
}

const defaultAction = ChatBulkAuditTaskAction.KEEP_IN_CURRENT_LEVEL;

const BulkAssignTaskActionsModal: React.FC<BulkAssignTaskActionsModalProps> = ({
  isOpen,
  onClose,
  attemptAudits,
}) => {
  const [selectedActions, setSelectedActions] = useState<Record<string, ChatBulkAuditTaskAction>>(
    ratingOptions.reduce((acc, rating) => {
      acc[rating.id] = defaultAction;
      return acc;
    }, {} as Record<string, ChatBulkAuditTaskAction>),
  );

  const [selectedReviewLevels, setSelectedReviewLevels] = useState<Record<string, string>>({});

  const handleActionChange = (rating: string, action: ChatBulkAuditTaskAction) => {
    setSelectedActions(prev => ({ ...prev, [rating]: action }));
  };

  const handleReviewLevelChange = (rating: string, reviewLevel: string) => {
    setSelectedReviewLevels(prev => ({ ...prev, [rating]: reviewLevel }));
  };

  const handleConfirm = () => {
    Object.entries(ratingMap).forEach(([rating, audits]) => {
      audits.forEach(audit => {
        audit.auditAction = selectedActions[rating];
        audit.sendToReviewLevel = selectedReviewLevels[rating];
      });
    });
    onClose();
  };

  const ratingMap = attemptAudits.reduce((acc, audit) => {
    const rating = audit.resultV2?.[0]?.result as AuditResultV2;
    if (rating !== undefined) {
      if (!acc[rating]) {
        acc[rating] = [];
      }
      acc[rating].push(audit);
    }
    return acc;
  }, {} as Record<AuditResultV2, AttemptAuditResults[]>);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content
        className="scaleui scale-theme scale-tailwind flex flex-col gap-2"
        maxWidth="700px"
      >
        <Dialog.Title>Bulk Assign Task Actions</Dialog.Title>
        <Dialog.Description>Select Actions for Each Rating</Dialog.Description>

        {ratingOptions.map(rating => (
          <div key={rating.id} className="flex items-center space-x-4">
            <div className="flex-shrink-0" style={{ width: '150px' }}>
              <Typography.Subtitle3>
                {`${rating.name} (${ratingMap[rating.id]?.length || 0})`}:
              </Typography.Subtitle3>
            </div>
            <SelectMenu
              options={actionOptions}
              selectedOptionId={selectedActions[rating.id]}
              setSelected={action =>
                handleActionChange(rating.id, action as ChatBulkAuditTaskAction)
              }
              disabled={(ratingMap[rating.id]?.length || 0) === 0}
            />
            {selectedActions[rating.id] ===
              ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_KEEP_RESPONSES && (
              <SelectMenu
                options={reviewLevelOptions}
                selectedOptionId={selectedReviewLevels[rating.id]}
                setSelected={reviewLevel =>
                  handleReviewLevelChange(rating.id, reviewLevel as string)
                }
                disabled={(ratingMap[rating.id]?.length || 0) === 0}
              />
            )}
          </div>
        ))}
        <div className="flex justify-end">
          <Button onClick={onClose} variant="neutral">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="ml-2"
            variant="primary"
            disabled={Object.keys(selectedActions).length === 0}
          >
            Confirm Actions
          </Button>
        </div>

        <Callout.Root size="1">
          <Callout.Icon>
            <Icon icon={faCircleInfo} label="info" size="2" />
          </Callout.Icon>

          <Callout.Text>- Actions will be performed after the audit is submitted</Callout.Text>
          <Callout.Text>
            - If you want more advanced options like sending back to original attempter, do not use
            this task action, instead, submit the audit and perform the action on compass
          </Callout.Text>
        </Callout.Root>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default BulkAssignTaskActionsModal;
